import dayjs, { Dayjs } from 'dayjs';
import orderBy from 'lodash/orderBy';
import { DEFAULT_BACKDATE_LIMIT, DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import {
  CardType,
  CurrencyType,
  DEFAULT_POOL_NAME,
  DisbursementMethod,
  EmployeeType,
  ICollectedTipsData,
  IEmployee,
  IEmployeePayrollTip,
  IPayee,
  IPayoutStateByDate,
  IPayPeriod,
  IReceivedTipsData,
  ISite,
  ITipCalculatorBaseTotals,
  ITipCalculatorResultReceived,
  PaymentType,
  PaymentWorkflowTypes,
  RuleFundSource,
  ShareRuleType,
  WithholdingsType,
} from 'types';
import { ICreatePaymentPayload, IPaymentPayout, IPaymentSetCalculations, IPaymentTotals, IPaymentWithholdings, ISubmitPaymentPayload } from 'types/PaymentSet';
import { centsToDollars, centsToNegativeDollarsDisplay } from './currency';
import { formatDateForApi } from './date';
import { calculateInstant, calculatePayroll } from './payout';
import { siteUsesWithholdings } from './site';
import { toTitleCase } from './strings';
import { getOwnerRuleProviderIds } from './tipCalculator';

export const getPaymentTypeIcon = (cardType: CardType | undefined) => {
  if (cardType?.toLowerCase() === CardType.AMERICAN_EXPRESS.toLowerCase()) {
    return 'AmericanExpress';
  }
  if (cardType?.toLowerCase() === CardType.DISCOVER.toLowerCase()) {
    return 'DiscoverCard';
  }
  if (cardType?.toLowerCase() === CardType.MASTER_CARD.toLowerCase()) {
    return 'Mastercard';
  }
  if (cardType?.toLowerCase() === CardType.VISA.toLowerCase()) {
    return 'Visa';
  }
  return 'Payment';
};

export const createPayeeFromEmployee = (employee: IEmployee, siteId: string, tipAmount = 0, cash = 0, note = ''): IPayee => {
  const employeeSite = employee.sites?.find((s) => s.id === siteId);
  const employeeSiteJob = employee.type === EmployeeType.ADMIN ? { title: toTitleCase(DEFAULT_ORG_ADMIN_JOB_TITLE) } : employeeSite?.job;
  const disbursementMethod = employeeSite?.disbursementMethod || DisbursementMethod.INSTANT;
  const gross = tipAmount + cash;
  return {
    id: employee.id,
    name: employee.name,
    tipAmount,
    gross,
    net: gross,
    cash,
    withholdingAmount: 0,
    holdbackAmount: 0,
    note,
    employee: {
      id: employee.id,
      name: employee.name,
      firstName: employee.firstName,
      lastName: employee.lastName,
      employeeId: employeeSite?.employeeId,
      job: employeeSiteJob,
      type: employee.type,
      role: employeeSite?.role,
      disbursementMethod,
      user: employee.user,
      withholdings: employeeSite?.withholdings,
    },
  };
};

interface IChartPoint {
  x: string;
  y: number;
  value: string;
  instant: number;
  payroll: number;
  cash: number;
}

export const paymentStatToChartPoints = (paymentStats: IPayoutStateByDate[]): IChartPoint[] => {
  if (paymentStats.length > 31) {
    // render chart-data as monthly totals when date-range is greater than 31 days
    const paymentStatsByMonth = paymentStats.reduce((acc, stat): Array<{ x: string; y: number; value: string; instant: number; payroll: number; cash: number }> => {
      const monthIndex = acc.findIndex((chartPoint) => chartPoint.x === dayjs(stat.date).format('MMM YYYY'));

      if (monthIndex >= 0) {
        // add to existing monthly chart-data if it exists...
        acc[monthIndex] = {
          ...acc[monthIndex],
          y: acc[monthIndex].y + centsToDollars(stat.net),
          value: `${+acc[monthIndex].value.split(' ')[0] + stat.count} payments`,
          instant: acc[monthIndex].instant + centsToDollars(stat.instant),
          payroll: acc[monthIndex].payroll + centsToDollars(stat.payroll),
          cash: acc[monthIndex].cash + centsToDollars(stat.cash),
        };
      } else {
        // ...otherwise create new monthly instance
        acc.push({
          x: dayjs(stat.date).format('MMM YYYY'),
          y: centsToDollars(stat.net),
          value: `${stat.count} payments`,
          instant: centsToDollars(stat.instant),
          payroll: centsToDollars(stat.payroll),
          cash: centsToDollars(stat.cash),
        });
      }

      return acc;
    }, []);

    return paymentStatsByMonth;
  }

  return paymentStats.map((paymentStat) => ({
    x: dayjs(paymentStat.date).format('MM/DD'),
    y: centsToDollars(paymentStat.net),
    value: `${paymentStat.count} payments`,
    instant: centsToDollars(paymentStat.instant),
    payroll: centsToDollars(paymentStat.payroll),
    cash: centsToDollars(paymentStat.cash),
  }));
};

/**
 * Builds an array of payment payouts from a list of tip calculator payees.
 * Each payee can result in multiple payout objects (e.g., for cash, payroll, and instant).
 *
 * @param {IPayee[]} payees - The list of payees to process.
 * @returns {IPaymentPayout[]} An array of payment payout objects.
 */
export const buildPaymentPayouts = (payees: IPayee[]): IPaymentPayout[] =>
  payees.flatMap((payee) => {
    const { employee, note, holdbackAmount = 0, autoGratuity = 0, cash = 0 } = payee;
    const payouts: IPaymentPayout[] = [];

    const basePayout = {
      currency: CurrencyType.USD,
      employeeId: employee.id,
      job: employee.job,
      notes: note || '',
      type: PaymentType.TIP,
    };

    const instant = calculateInstant(payee);
    const payroll = calculatePayroll(payee);
    const payrollAutoGratuity = Math.min(payroll, autoGratuity);
    const instantGross = instant + holdbackAmount;
    const instantAutoGratuity = Math.min(instantGross, autoGratuity - payrollAutoGratuity);

    if (payroll > 0) {
      payouts.push({
        ...basePayout,
        disbursementMethod: DisbursementMethod.PAYROLL,
        gross: payroll,
        net: payroll,
        holdback: 0,
        ...(autoGratuity > 0 && { autoGratuity: payrollAutoGratuity }),
      });
    }

    if (instant > 0) {
      payouts.push({
        ...basePayout,
        disbursementMethod: DisbursementMethod.INSTANT,
        gross: instantGross,
        net: instant,
        holdback: holdbackAmount,
        ...(autoGratuity > 0 && { autoGratuity: instantAutoGratuity }),
      });
    }

    if (cash > 0) {
      payouts.push({
        ...basePayout,
        disbursementMethod: DisbursementMethod.CASH,
        gross: cash,
        net: cash,
        holdback: 0,
        ...(autoGratuity > 0 && { autoGratuity: autoGratuity - payrollAutoGratuity - instantAutoGratuity }),
      });
    }

    return payouts;
  });

/**
 * Constructs the payload required for creating a payment request.
 *
 * @param {ISubmitPaymentPayload} paymentData - Object containing payment details: payees list, description, and payment date.
 * @param {string} currentSiteId - The ID of the site where the payment is being made.
 * @param {PaymentWorkflowTypes} workflow - The type of payment workflow (e.g., STANDARD, UPLOAD).
 * @param {IPaymentWithholdings} [withholdings] - Optional withholding configuration for the payment.
 * @returns {ICreatePaymentPayload} The formatted payload object for the create payment API endpoint.
 */
export const createPaymentPayload = ({ payees, description, paymentDate }: ISubmitPaymentPayload, currentSiteId: string, workflow: PaymentWorkflowTypes, withholdings?: IPaymentWithholdings): ICreatePaymentPayload => ({
  ...(description && { description }),
  paymentDate: formatDateForApi(paymentDate),
  payouts: buildPaymentPayouts(payees),
  siteId: currentSiteId,
  workflow,
  withholdings,
});

/**
 * Constructs the withholdings object for a payment payload.
 *
 * @param {IPayee[]} payees - An array of payee objects with their calculated withholdings.
 * @param {ISite} site - Current site with the default withholding settings.
 * @param {IPayPeriod} payPeriod - The pay period for which the withholdings are being calculated.
 * @param {IEmployeePayrollTip[]} [payrollTips] - Optional payroll data for employees to determine current payroll totals.
 * @returns {IPaymentWithholdings} The complete payment withholdings object.
 */
export const buildPaymentWithholdings = (payees: IPayee[], site: ISite, payPeriod: IPayPeriod, payrollTips?: IEmployeePayrollTip[]): IPaymentWithholdings => {
  if (!siteUsesWithholdings(site)) return undefined;

  const payrollMap = new Map(payrollTips?.map((p) => [p.employee.id, p.payroll.total]));
  const withholdingEmployees = payees.reduce((acc, payee) => {
    if (payee.employee.withholdings?.type === WithholdingsType.NONE) return acc;

    acc.push({
      id: payee.employee.id,
      amount: payee.withholdingAmount,
      currentPayroll: payrollMap.get(payee.employee.id) || 0,
      settings: {
        ...(payee.employee.withholdings?.type === WithholdingsType.CUSTOM ? payee.employee.withholdings : site.settings.withholdings),
        type: payee.employee.withholdings?.type || WithholdingsType.DEFAULT,
      },
    });
    return acc;
  }, []);
  if (!withholdingEmployees.length) return undefined;

  return {
    employees: withholdingEmployees,
    payPeriod,
  };
};

export const calculateOverdraftStatus = (netInstant: number, overdraftLimit: number, siteBalance = 0) => {
  if (netInstant <= 0) return { showOverdraft: false, overdraftContinue: true };

  const newBalance = netInstant - siteBalance;
  const totalBalancePlusOverdraft = siteBalance + overdraftLimit;

  if (netInstant > siteBalance && netInstant <= totalBalancePlusOverdraft) {
    return {
      showOverdraft: true,
      overdraftContinue: true,
      message: `Your new balance will be ${centsToNegativeDollarsDisplay(newBalance, true)}. Your limit is ${centsToNegativeDollarsDisplay(overdraftLimit, true)}.`,
    };
  }

  if (netInstant > totalBalancePlusOverdraft) {
    return {
      showOverdraft: true,
      overdraftContinue: false,
      message: `Your new balance is below the limit of ${centsToNegativeDollarsDisplay(overdraftLimit, true)}. Payment cannot be accepted.`,
    };
  }

  return { showOverdraft: false, overdraftContinue: true };
};

export const calculatePaymentTotals = (payees: IPayee[]): IPaymentTotals =>
  payees.reduce(
    (acc, payee) => {
      const tipAmount = payee.tipAmount || 0;
      const holdbackAmount = payee.holdbackAmount || 0;
      const cash = payee.cash || 0;

      acc.gross += tipAmount + cash;
      acc.holdbacks += holdbackAmount;
      acc.cash += cash;

      if (payee.employee?.disbursementMethod === DisbursementMethod.INSTANT) {
        const withholding = payee.withholdingAmount || 0;
        const instant = tipAmount - withholding;
        acc.instant += instant;
        acc.payNow += instant - holdbackAmount;
        acc.payroll += withholding;
      } else if (payee.employee?.disbursementMethod === DisbursementMethod.PAYROLL) {
        acc.payroll += tipAmount;
      }
      return acc;
    },
    { gross: 0, payNow: 0, cash: 0, instant: 0, payroll: 0, holdbacks: 0 }
  );

export interface IPaymentDayInfo {
  title: string;
  message: string | undefined;
}

/**
 * The title and message for a selected payment date in a closed pay period, outside the site's payable window (after its today,
 * before its backdate limit) or already paid, in that order of precedence; null when there is nothing to say.
 *
 * @param {object} options - The options object.
 * @param {boolean} [options.isInClosedPayPeriod] - Whether the selected date falls within a closed pay period.
 * @param {boolean} options.isDayPayable - Whether the site can pay the selected date: not after its today, not before its backdate limit.
 * @param {boolean} options.isDayPaid - Whether one or more payments have already been made on the selected date.
 * @param {number} options.backdateLimit - The number of previous business days the site allows for backdating.
 * @returns {IPaymentDayInfo} An object containing the warning title and an optional descriptive message.
 */
export const buildPaymentDayInfo = ({ isInClosedPayPeriod, isDayPayable, isDayPaid, backdateLimit }: { isInClosedPayPeriod?: boolean; isDayPayable: boolean; isDayPaid: boolean; backdateLimit: number }): IPaymentDayInfo => {
  if (isInClosedPayPeriod) {
    return {
      title: 'This day falls within a closed pay period.',
      message: `New payments can't be added.${isDayPaid ? ' Prior payments for this day are shown below.' : ''}`,
    };
  }

  if (!isDayPayable) {
    return {
      title: `This business day is closed for payments.${isDayPaid ? ' Prior payments are shown below.' : ''}`,
      message: backdateLimit > 0 ? `Payments are allowed for today and ${backdateLimit} previous business days.` : undefined,
    };
  }

  if (isDayPaid) {
    return {
      title: 'Payments for this date have already been made',
      message: 'Please verify that you are not making a duplicate payment.',
    };
  }

  return null;
};

/**
 * Returns the current date adjusted to the site's local timezone.
 *
 * @param siteTimezone - The IANA timezone string for the site (e.g. 'America/New_York').
 * @returns A Dayjs instance representing today in the site's timezone.
 */
export const getTodayWithSiteTimezone = (siteTimezone?: string) => dayjs().tz(siteTimezone);

/**
 * Returns the earliest date on which a payment can be backdated for a given site.
 *
 * @param siteTimezone - The IANA timezone string for the site (e.g. 'America/New_York').
 * @param backdateLimit - The number of previous business days allowed for backdating. Defaults to `DEFAULT_BACKDATE_LIMIT`.
 * @returns A Dayjs instance representing the earliest payable date.
 */
export const getEarliestPayableDate = (siteTimezone: string, backdateLimit = DEFAULT_BACKDATE_LIMIT) => getTodayWithSiteTimezone(siteTimezone).subtract(backdateLimit, 'days');

/**
 * Determines whether a given date is a valid business date for payment.
 * A date is valid if it is on or before the site's today, and on or after the earliest payable date based on the site's backdate limit.
 *
 * @param date - The date to validate, either as a `MM-DD-YYYY` string or a Dayjs instance.
 * @param siteTimezone - The IANA timezone string for the site (e.g. 'America/New_York').
 * @param backdateLimit - The number of previous business days allowed for backdating. Defaults to `DEFAULT_BACKDATE_LIMIT`.
 * @returns `true` if the date is valid for payment, `false` otherwise.
 */
export const isValidBusinessDate = (date: string | Dayjs, siteTimezone: string, backdateLimit = DEFAULT_BACKDATE_LIMIT) => {
  const businessDate = typeof date === 'string' ? dayjs(date, 'MM-DD-YYYY', true) : date;
  if (!businessDate.isValid()) return false;
  const pickedDate = businessDate.format('YYYY-MM-DD');
  const siteToday = getTodayWithSiteTimezone(siteTimezone).format('YYYY-MM-DD');
  const siteEarliest = getEarliestPayableDate(siteTimezone, backdateLimit).format('YYYY-MM-DD');
  return pickedDate <= siteToday && pickedDate >= siteEarliest;
};

const SPLIT_CHECK_PREFIX = 'Split Check #';
const IGNORED_CHECK_PREFIX = 'Ignored Check #';

const buildCollectedTotals = (totals: ITipCalculatorBaseTotals) => ({
  cashTips: totals.tip.cash,
  cashAutograts: totals.autoGratuity.cash,
  ccTips: totals.tip.creditCard,
  ccAutograts: totals.autoGratuity.creditCard,
  ccFee: totals.tip.creditCardFee + totals.autoGratuity.creditCardFee,
  net: totals.net,
});

/**
 * Flattens tip calculator results into a sorted table of tips collected per employee.
 * Each employee contributes a base row for their segment totals, plus additional rows for any
 * split-check contributions and ignored checks.
 *
 * @param {IPaymentSetCalculations} calculations - The tip calculator results for the payment set.
 * @param {{ field: keyof ICollectedTipsData; direction?: 'asc' | 'desc' }} sort - The field and direction used to sort the resulting rows.
 * @returns {ICollectedTipsData[]} The sorted list of collected-tips rows.
 */
export const formatCollectedTipsData = (calculations: IPaymentSetCalculations, sort: { field: keyof ICollectedTipsData; direction?: 'asc' | 'desc' }): ICollectedTipsData[] => {
  const rows: ICollectedTipsData[] = [];

  calculations?.results.forEach((result) => {
    const ownerRuleProviderIds = getOwnerRuleProviderIds(result.rules);

    result.employees.forEach((employee) => {
      const payee = employee.name;
      const jobTitle = ownerRuleProviderIds.has(employee.provider?.id) ? undefined : employee.job.title;

      rows.push({ id: rows.length, payee, jobTitle, daySegment: result.segment, ...buildCollectedTotals(employee.totals) });

      employee.splitChecks?.contributions.forEach((contribution) => {
        rows.push({
          id: rows.length,
          payee,
          jobTitle: undefined,
          daySegment: `${SPLIT_CHECK_PREFIX}${contribution.displayId}`,
          ...buildCollectedTotals(contribution.totals),
        });
      });

      employee.ignoredChecks?.forEach((check) => {
        rows.push({
          id: rows.length,
          payee,
          jobTitle: undefined,
          daySegment: `${IGNORED_CHECK_PREFIX}${check.displayId}`,
          ...buildCollectedTotals(check.totals),
        });
      });
    });
  });

  return orderBy(rows, sort.field, sort.direction);
};

const getReceivedFromLabel = (received: ITipCalculatorResultReceived): string => {
  switch (received.type) {
    case ShareRuleType.CATEGORY:
    case ShareRuleType.CATEGORY_GROUP:
      return `${received.from.name} (${received.category})`;
    case ShareRuleType.DEFAULT_POOL:
      return DEFAULT_POOL_NAME;
    default:
      return received.from.name;
  }
};

/**
 * Flattens tip calculator results into a sorted table of tips received per employee.
 * For each employee, emits a row for any tips they retained (`Self`), a row per pool/category
 * received from another source, and a row per split-check received.
 *
 * @param {IPaymentSetCalculations} calculations - The tip calculator results for the payment set.
 * @param {{ field: keyof IReceivedTipsData; direction?: 'asc' | 'desc' }} sort - The field and direction used to sort the resulting rows.
 * @returns {IReceivedTipsData[]} The sorted list of received-tips rows.
 */
export const formatReceivedTipsData = (calculations: IPaymentSetCalculations, sort: { field: keyof IReceivedTipsData; direction?: 'asc' | 'desc' }): IReceivedTipsData[] => {
  const rows: IReceivedTipsData[] = [];

  calculations?.results.forEach((result) => {
    const ownerRuleProviderIds = getOwnerRuleProviderIds(result.rules);

    result.employees.forEach((employee) => {
      const payee = employee.name;
      const duration = employee.totals.totalMinWorked;

      if (employee.retained?.total) {
        rows.push({
          payee,
          jobTitle: ownerRuleProviderIds.has(employee.provider?.id) ? undefined : employee.job.title,
          daySegment: result.segment,
          duration,
          receivedFrom: 'Self',
          receivedTips: employee.retained.tips,
          receivedAutograts: employee.retained.autoGratuities,
        });
      }

      employee.received?.forEach((received) => {
        rows.push({
          payee,
          jobTitle: employee.job.title,
          daySegment: result.segment,
          duration,
          receivedFrom: getReceivedFromLabel(received),
          receivedTips: received.sources.find((item) => item.source === RuleFundSource.TIPS)?.total ?? 0,
          receivedAutograts: received.sources.find((item) => item.source === RuleFundSource.AUTO_GRATUITIES)?.total ?? 0,
        });
      });

      employee.splitChecks?.received.forEach((received) => {
        rows.push({
          payee,
          jobTitle: undefined,
          receivedFrom: `${SPLIT_CHECK_PREFIX}${received.displayId}`,
          receivedTips: received.totals.tips,
          receivedAutograts: received.totals.autoGratuities,
        });
      });
    });
  });

  return orderBy(rows, sort.field, sort.direction);
};
